import { Controller } from "@hotwired/stimulus"
import { computePosition, offset, flip, shift, autoUpdate } from "@floating-ui/dom"

export default class extends Controller {
  connect() {
    this.cleanupAutoUpdate = null
    this.outsideClickHandler = null
    this.escapeHandler = null
    this.currentPopoverElement = null
  }

  disconnect() {
    this.close()
  }

  async showFor(triggerElement, { html, placement = "right-start", popoverClass = "", onClose = null } = {}) {
    if (!triggerElement || !html) return null

    this.close()
    this.onClose = onClose

    this.currentPopoverElement = document.createElement("div")
    this.currentPopoverElement.className =
      "fixed z-[80] min-w-[280px] max-w-[340px] rounded-xl border border-neutral-200 bg-white text-neutral-900 shadow-2xl"

    if (popoverClass) {
      this.currentPopoverElement.className = `${this.currentPopoverElement.className} ${popoverClass}`
    }

    this.currentPopoverElement.innerHTML = html
    document.body.appendChild(this.currentPopoverElement)

    await this.#updatePosition(triggerElement, placement)

    this.cleanupAutoUpdate = autoUpdate(triggerElement, this.currentPopoverElement, () => {
      this.#updatePosition(triggerElement, placement)
    })

    this.outsideClickHandler = (event) => {
      if (!this.currentPopoverElement) return
      if (this.currentPopoverElement.contains(event.target)) return
      if (triggerElement.contains(event.target)) return
      this.close()
    }

    this.escapeHandler = (event) => {
      if (event.key === "Escape") this.close()
    }

    document.addEventListener("click", this.outsideClickHandler, true)
    document.addEventListener("keydown", this.escapeHandler)

    return this.currentPopoverElement
  }

  close() {
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate()
      this.cleanupAutoUpdate = null
    }

    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler, true)
      this.outsideClickHandler = null
    }

    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler)
      this.escapeHandler = null
    }

    if (this.currentPopoverElement) {
      this.currentPopoverElement.remove()
      this.currentPopoverElement = null
    }

    if (typeof this.onClose === "function") {
      this.onClose()
    }
    this.onClose = null
  }

  async #updatePosition(triggerElement, placement) {
    if (!this.currentPopoverElement) return

    const { x, y } = await computePosition(triggerElement, this.currentPopoverElement, {
      placement,
      middleware: [offset(10), flip(), shift({ padding: 8 })]
    })

    Object.assign(this.currentPopoverElement.style, {
      left: `${x}px`,
      top: `${y}px`
    })
  }
}
